import React from 'react';
import { graphql } from 'gatsby';
import styled, { css } from 'styled-components';
import { standardColours, brandColours } from '../styles';
import Accordion from './Accordion';
import ImageCards from './ImageCards';
import HighlightedContent from './HighlightedContent';
import ContentCards from './ContentCards';
import FeaturedLogos from './FeaturedLogos';
import FeaturedSpas from './FeaturedSpas';
import FeaturedSpaGroups from './FeaturedSpaGroups';
import IconContent from './IconContent';
import Content from './Content';
import ContainedImage from './ContainedImage';
import PopularSpas from './PopularSpas';
import SpaCtas from './SpaCtas';
import Steps from './Steps';
import ContentLinksList from './ContentLinksList';
import SpaCtaGroups from './SpaCtaGroups';
import Testimonials from './Testimonials';
import HeadingContent from './HeadingContent';
import ImageContent from './ImageContent';
import Video from './Video';

const StyledWrapper = styled.div`
  padding: 1px 0;

  ${({ sectionBehaviour }) => {
    if (sectionBehaviour === 'BLUE') {
      return css`
        background: linear-gradient(
          ${standardColours.transparent} 0,
          ${brandColours.senary} 50px,
          ${brandColours.senary} calc(100% - 50px),
          ${standardColours.transparent} 100%
        );
      `;
    }
  }}
`;

const ModularBlocks = ({ items, affilateName }) => {
  const wrappedItems = [];

  let groupNum = 0;
  let groupItems = [];

  items.forEach((item, i) => {
    const nextItem = items[i + 1];

    groupItems.push(item);

    if (
      (nextItem && nextItem.sectionBehaviour !== item.sectionBehaviour) ||
      !nextItem
    ) {
      wrappedItems[groupNum] = {
        sectionBehaviour: item.sectionBehaviour,
        items: groupItems,
      };

      groupNum++;
      groupItems = [];
    }
  });

  return wrappedItems.map(({ sectionBehaviour, items }) => {
    const WrapperComponent =
      sectionBehaviour === 'BLUE' ? StyledWrapper : React.Fragment;

    return (
      <WrapperComponent sectionBehaviour={sectionBehaviour}>
        {items.map((item, id) => (
          <React.Fragment key={id}>
            {item.accordion !== null && (
              <Accordion
                heading={item.accordion.heading}
                items={item.accordion.items}
                isFaqs={item.accordion.isFaqs}
              />
            )}
            {item.banners !== null && (
              <ImageCards
                heading={item.banners.title}
                items={item.banners.banners}
              />
            )}
            {item.callToAction !== null && (
              <HighlightedContent
                heading={item.callToAction.heading}
                text={item.callToAction.text}
                subHeading={item.callToAction.subHeading}
                button={item.callToAction.button}
                centered={
                  item.callToAction.style === 'CENTER_ALIGNED' ? true : false
                }
              />
            )}
            {item.cardDeck !== null && (
              <ContentCards
                heading={item.cardDeck.heading}
                items={item.cardDeck.items}
                alt={true}
              />
            )}
            {item.clientDeck !== null && (
              <FeaturedLogos heading={item.clientDeck.heading} />
            )}
            {item.featuredSpasCarousel !== null && (
              <FeaturedSpas
                heading={item.featuredSpasCarousel.title}
                items={item.featuredSpasCarousel.spas}
                alt={true}
              />
            )}
            {item.frequentlyAskedQuestions !== null && (
              <Accordion
                heading={item.frequentlyAskedQuestions.title}
                items={item.frequentlyAskedQuestions.questions}
                isFaqs={true}
              />
            )}
            {item.heading !== null && (
              <IconContent
                heading={item.heading.heading}
                htmlId={item.heading.header?.htmlId}
                text={item.heading.subHeading}
              />
            )}
            {item.html !== null && (
              <Content html={item.html.html} contain={true} />
            )}
            {item.image !== null && (
              <ContainedImage
                image={item.image.image}
                caption={item.image.caption}
              />
            )}
            {item.popularSpas !== null && (
              <PopularSpas
                heading={item.popularSpas.heading}
                items={item.popularSpas.items}
              />
            )}
            {item.spaCarouselSet !== null && (
              <FeaturedSpaGroups
                heading={item.spaCarouselSet.title}
                text={item.spaCarouselSet.subTitle}
                items={item.spaCarouselSet.items}
              />
            )}
            {item.spaDeck !== null && (
              <SpaCtas
                heading={item.spaDeck.heading}
                text={item.spaDeck.subHeading}
                items={item.spaDeck.items}
                affilateName={affilateName}
              />
            )}
            {item.spaPackageCarousel !== null && (
              <FeaturedSpas
                heading={item.spaPackageCarousel.header.heading}
                items={item.spaPackageCarousel.spas}
                alt={true}
              />
            )}
            {item.steps !== null && (
              <Steps
                heading={item.steps.title}
                items={item.steps.steps}
                content={item.steps.contact}
              />
            )}
            {item.styledList !== null && (
              <ContentLinksList
                content={item.styledList.html}
                links={item.styledList.items}
              />
            )}
            {item.taggedSpas !== null && (
              <SpaCtaGroups items={item.taggedSpas.regions} />
            )}
            {item.testimonial !== null && (
              <Testimonials
                heading={item.testimonial.heading}
                items={item.testimonial.items}
              />
            )}
            {item.twoColumn !== null &&
              (item.twoColumn.style === 'LEFT_HEADING_RIGHT_TEXT' ? (
                <HeadingContent
                  heading={item.twoColumn.heading}
                  content={item.twoColumn.text}
                />
              ) : (
                <ImageContent
                  image={item.twoColumn.image}
                  heading={item.twoColumn.heading}
                  text={item.twoColumn.text}
                  flip={
                    item.twoColumn.style === 'LEFT_TEXT_RIGHT_IMAGE' ||
                    item.twoColumn.style === 'FULL_WIDTH_LEFT_TEXT_RIGHT_IMAGE'
                      ? true
                      : false
                  }
                  alt={
                    item.twoColumn.style ===
                      'FULL_WIDTH_LEFT_IMAGE_RIGHT_TEXT' ||
                    item.twoColumn.style === 'FULL_WIDTH_LEFT_TEXT_RIGHT_IMAGE'
                      ? true
                      : false
                  }
                />
              ))}
            {item.video !== null && <Video url={item.video.videoUrl} />}
          </React.Fragment>
        ))}
      </WrapperComponent>
    );
  });
};

export default ModularBlocks;

export const ModularBlocksFragment = graphql`
  fragment ModularBlocksFragment on SPASEEKERS_ContentBlock {
    accordion {
      heading
      items {
        heading
        content: text
      }
      isFaqs
    }
    banners {
      title
      banners {
        heading: text
        image {
          url
          title
        }
        url
      }
    }
    callToAction {
      heading
      text
      subHeading
      button {
        text
        url
      }
      style
    }
    cardDeck {
      heading
      items {
        heading
        text
        button {
          text
          url
        }
      }
    }
    clientDeck {
      heading
    }
    featuredSpasCarousel {
      title
      spas {
        ...ContentFeaturedSpaCardFragment
      }
    }
    frequentlyAskedQuestions {
      title
      questions {
        heading: title
        content
      }
    }
    heading {
      heading
      subHeading
      header {
        htmlId
      }
    }
    html {
      html
    }
    image {
      image {
        url
        title
      }
      caption
    }
    popularSpas {
      heading
      items {
        title
        subtitle
        image {
          url
          title
        }
        url
      }
    }
    spaCarouselSet {
      title
      subTitle
      items {
        title
        topContent
        spas {
          ...SpaCardFragment
        }
        bottomContent
      }
    }
    spaDeck {
      heading
      subHeading
      items {
        image {
          url
          title
        }
        logo {
          url
          title
        }
        heading: title
        subheading: subtitle
        text
        link: button {
          text
          url
        }
      }
    }
    spaPackageCarousel {
      header {
        heading
      }
      spas {
        ...ContentSpaPackageCardFragment
      }
    }
    steps {
      title
      steps {
        title
        text
      }
      contact
    }
    styledList {
      html
      items {
        name
        url
      }
    }
    taggedSpas {
      regions {
        name
        spas {
          heading: name
          url
          image {
            url
            title
          }
          location
          text: description
          minPrice
          isPricePerPerson
        }
      }
    }
    testimonial {
      heading
      items {
        text
        image {
          url
          title
        }
        name
        subHeading
      }
    }
    twoColumn {
      heading
      text
      image {
        url
        title
      }
      style
    }
    video {
      videoUrl
    }
    sectionBehaviour
  }
`;
